// Namespace „Mail" — die Texte des Mail-Cockpits:
//   pages/Mail.tsx        (Posteingang, Lesen, Composer, Ordner, Suche,
//                          Vorlagen, Fragen, Nachverfolgung, Abwesenheit)
//   mail/ContactCreate.tsx (Kontakt/Lead aus einer Mail anlegen)
//   mail/shortcuts.ts      (Beschreibungen der Tastenkürzel)
//
// WICHTIG (docs/i18n.md §Ebenen): hier stehen ausschließlich Texte der
// OBERFLÄCHE. Die Sprache erzeugter Antworten und Nachfass-Mails richtet sich
// nach dem Gegenüber (app/mail/reply.py, chase_language) und hat mit dieser
// Einstellung nichts zu tun.
//
// NICHT hier drin und bewusst deutsch, auch in der englischen Oberfläche:
//   * Datums-, Uhrzeit- und Zahl-FORMATE (`fmtListTime`, `fmtFullDate`,
//     `fmtStand`, `fmtDateDe`, `fuSlotLabel`, `fuAntwortZeit`, alle
//     `de-DE`-Aufrufe und `ui/germanDate.ts`) — inklusive `TT.MM.JJJJ` als
//     Eingabemaske und des Wortes „Uhr" hinter einer Uhrzeit.
//   * Die TASTEN selbst (`shortcuts.ts::keys`: „Entf", „Umschalt+Leertaste",
//     „Strg+Umschalt+A"). Sie sind Outlook-nativ und die Bindung, nicht die
//     Beschreibung — übersetzt wird nur, was sie TUN.
//   * Server-Sätze: `apiFehlertext`, `err.message` aus Salesforce-Ablehnungen,
//     die Warnungen des Kontakt-/Lead-Vorschlags, `abstain_reason`-Detailtext
//     der Abfrage-Engine (docs/i18n.md §5).
//   * INHALTE, die versendet oder gespeichert werden: Signatur-HTML,
//     aufgelöste Vorlagen (`preview_md`), Mail-Bodies, Betreff-Präfixe
//     „Re:"/„Fwd:" (die stehen in der GESENDETEN Mail).
export const mail = {
  // ── Seite: Kopf + Reiter ──────────────────────────────────────────────────
  seite: {
    titel: "Mail",
    stand: (zeit: string) => `Stand ${zeit}`,
    reiterPosteingang: "Posteingang",
    reiterNachverfolgung: "Nachverfolgung",
    reiterVorlagen: "Vorlagen",
    reiterFragen: "Fragen",
    abwesenheitsnotiz: "Abwesenheitsnotiz",
    neueMail: "Neue Mail",
    // E1-Entlastung: der Seitenkopf trägt nur noch „Neue Mail", den
    // Postfach-Wähler, das Ansicht-Menü (Dichte) und den ···-Überlauf
    // (Aktualisieren · Abwesenheitsnotiz · Tastenkürzel). Nichts fällt weg —
    // alles zieht in genau eines der beiden Menüs.
    ansicht: "Ansicht",
    ansichtTitel: "Ansicht — wie tief die Liste geladen wird",
    ansichtDichte: "Dichte",
    weitereBefehle: "Weitere Befehle",
    tiefeAria:
      "Mail-Tiefe (wie tief Posteingang, Gesendet und Entwürfe geladen werden)",
    // Die drei Stufen. `ui/mailInboxDepth.ts` hält nur noch Werte + Presets
    // (top/days) — Zahlen sind keine Sprache, Beschriftungen schon. Wird auch
    // von der Einstellungs-Karte „Standard-Tiefe" gelesen: EINE Quelle.
    tiefe: {
      kompakt: {
        label: "Kompakt",
        hinweis: "Die letzten 25 Nachrichten der vergangenen 14 Tage.",
      },
      erweitert: {
        label: "Erweitert",
        hinweis: "Die letzten 50 Nachrichten der vergangenen 30 Tage.",
      },
      maximal: {
        label: "Maximal",
        hinweis: "Die letzten 100 Nachrichten der vergangenen 90 Tage.",
      },
    },
    nichtErreichbarTitel: "Mail-Service nicht erreichbar",
    nichtErreichbarHinweis:
      "Dein Postfach lässt sich gerade nicht laden. Versuch es in einem Moment erneut.",
    nichtFreigeschaltetTitel: "Dein Postfach ist noch nicht für Atlas freigeschaltet",
    nichtFreigeschaltetHinweis:
      "Sobald der E-Mail-Sync für dich aktiviert ist, erscheinen deine Nachrichten hier automatisch.",
  },

  // ── Postfach-Umschalter (eigenes + Funktionspostfächer) ───────────────────
  postfach: {
    wechseln: "Postfach wechseln",
    // Beschriftung des EIGENEN Postfachs. Der Server liefert dafür den
    // Schlüssel `display_key === "own_mailbox"` (app/schemas/mail.py::MailboxOut);
    // die Namen der Funktionspostfächer sind dagegen Daten und bleiben, wie sie
    // aus dem email-service kommen.
    eigenes: "Mein Postfach",
  },

  // ── Nachrichtenliste + Zeilen-Kontextmenü ─────────────────────────────────
  liste: {
    ungelesen: "Ungelesen",
    an: "An:",
    keinEmpfaenger: "Kein Empfänger",
    unbekannt: "Unbekannt",
    anhang: "Anhang",
    inNachverfolgung: "In Nachverfolgung",
    archivieren: "Archivieren",
    aktionen: "Aktionen",
    aktionenTitel: "Aktionen (auch per Rechtsklick)",
    keinBetreff: "(kein Betreff)",
    wichtig: "Wichtig",
    sfNichtErreichbar: "Salesforce nicht erreichbar",
    mehrLaden: "Mehr laden",
    tiefeEntwuerfe: (n: number) =>
      `${n === 0 ? "Keine" : n} ${n === 1 ? "Entwurf" : "Entwürfe"} geladen · ohne Zeitraum-Begrenzung`,
    tiefeNachrichten: (n: number, tage: number) =>
      `${n === 0 ? "Keine" : n} ${n === 1 ? "Nachricht" : "Nachrichten"} geladen · letzte ${tage} Tage`,
    leerOrdnerTitel: "Ordner ist leer",
    leerOrdnerHinweis:
      "In diesem Ordner liegen im gewählten Zeitraum keine Nachrichten.",
    leerTrefferTitel: "Keine Treffer",
    leerTrefferHinweis:
      "Für deine Suche gibt es keine Nachrichten. Suchbegriff oder Filter anpassen.",
    leerPostfachTitel: "Keine Nachrichten",
    leerPostfachHinweis: "Dein Postfach ist im gewählten Zeitraum leer.",
    keineMailGewaehltTitel: "Keine Mail ausgewählt",
    keineMailGewaehltHinweis:
      "Wähle links eine Nachricht, um sie samt Salesforce-Kontext zu lesen.",
    keinEntwurfGewaehltTitel: "Keinen Entwurf ausgewählt",
    keinEntwurfGewaehltHinweis:
      "Wähle links einen Entwurf, um ihn im Verfassen-Fenster weiterzuschreiben.",
    // Zeilen-Kontextmenü (Rechtsklick / Umschalt+F10 / Langdruck)
    menuAria: (betreff: string) => `Aktionen für „${betreff}"`,
    menuAllenAntworten: "Allen antworten",
    menuFreiAntworten: "Frei antworten",
    menuWeiterleiten: "Weiterleiten",
    menuVorlage: "Vorlage einfügen",
    menuTermin: "Termin aus dieser Mail",
    menuTerminTitel: (taste: string) => `Termin aus dieser Mail (${taste})`,
    menuNachverfolgen: "Zur Nachverfolgung markieren",
    menuAlsUngelesen: "Als ungelesen markieren",
    menuAlsGelesen: "Als gelesen markieren",
    menuArchivieren: "Archivieren",
    menuVerschieben: "In Ordner verschieben",
    menuLoeschenTitel: (taste: string) => `Löschen (${taste})`,
  },

  // ── Suche + Filter ────────────────────────────────────────────────────────
  suche: {
    platzhalter: (taste: string) =>
      `Postfach durchsuchen — Eingabetaste sucht (${taste})`,
    titel: (tasten: string) =>
      `Postfach durchsuchen: Suchbegriff eingeben, dann Eingabetaste. Feld anspringen mit ${tasten}, leeren mit Esc.`,
    aria: (tasten: string) =>
      `Postfach durchsuchen — mit der Eingabetaste suchen (Feld anspringen: ${tasten})`,
    laeuft: "Suche läuft …",
    leeren: "Suche leeren",
    leerenTitel: "Suche leeren (Esc)",
    absender: "Von (Absender)",
    absenderAria: "Filter Absender-Adresse",
    // E1-Entlastung: Absender + Zeitraum + Schnellwahl liegen im Filter-
    // Popover. Ein Filter, den man nicht sieht, muss sich MELDEN — daher der
    // Zähler am Knopf und die Zeile darunter.
    filter: "Filter",
    filterTitel: "Filter — Absender und Zeitraum",
    filterAria: (n: number) =>
      n === 0 ? "Filter öffnen" : `Filter öffnen — ${n} aktiv`,
    filterAktiv: (n: number) => `${n} Filter aktiv`,
    filterZuruecksetzen: "Zurücksetzen",
    schnellwahl: "Schnellwahl",
    zeitraum: "Zeitraum",
    von: "von",
    bis: "bis",
    vonAria: "Filter Zeitraum von (TT.MM.JJJJ)",
    vonTitel: "Zeitraum von (TT.MM.JJJJ)",
    bisAria: "Filter Zeitraum bis (TT.MM.JJJJ)",
    bisTitel: "Zeitraum bis (TT.MM.JJJJ)",
    tage7: "7 Tage",
    tage30: "30 Tage",
    datumBeideFalsch: "Von und bis bitte als TT.MM.JJJJ angeben (z. B. 01.02.2026).",
    datumVonFalsch: "Von bitte als TT.MM.JJJJ angeben (z. B. 01.02.2026).",
    datumBisFalsch: "Bis bitte als TT.MM.JJJJ angeben (z. B. 01.02.2026).",
    // Beschreibung einer abgeschickten Abfrage („»Rechnung« · von x · ab y")
    begriff: (q: string) => `»${q}«`,
    vonWem: (v: string) => `von ${v}`,
    abDatum: (d: string) => `ab ${d}`,
    bisDatum: (d: string) => `bis ${d}`,
    zeigtPosteingang: "der Posteingang",
    zeigtErgebnis: (frage: string) => `das Ergebnis zu ${frage}`,
    zeigtFilterErgebnis: (frage: string) => `das Ergebnis der Filter-Suche (${frage})`,
    veraltetLaeuft: (was: string) => `Suche läuft … Unten steht noch ${was}.`,
    veraltet: (was: string) =>
      `Unten steht noch ${was} — nicht das Ergebnis zu deiner Eingabe. Eingabetaste sucht.`,
    bereichGanzesPostfachFest:
      "Gesucht im ganzen Postfach — dieser Ordner lässt sich nicht einzeln durchsuchen.",
    bereichGanzesPostfach: "Gesucht im ganzen Postfach.",
    bereichNurOrdner: (ordner: string) => `Nur in „${ordner}" suchen`,
    bereichInOrdner: (ordner: string) => `Gesucht in „${ordner}".`,
    bereichWechselGanzesPostfach: "Im ganzen Postfach suchen",
    gedeckelt: (n: number) =>
      `Nicht vollständig: Microsoft liefert zu dieser Suche höchstens die ${n} jüngsten Treffer. Grenze den Suchbegriff oder den Zeitraum ein, um ältere zu sehen.`,
    gedeckeltEnde: (n: number) =>
      `Ende der Liste — hier hört Microsoft auf, nicht deine Suche. Zu häufigen Begriffen gibt Graph nicht mehr als ${n} Treffer heraus.`,
    fehler:
      "Die Suche konnte nicht ausgeführt werden. Das ist ein Fehler der Anwendung, nicht deines Suchbegriffs.",
  },

  // ── Ordner: Chips, Aufklapper, Verschieben-Dialog ─────────────────────────
  ordner: {
    posteingang: "Posteingang",
    gesendet: "Gesendet",
    entwuerfe: "Entwürfe",
    ordner: "Ordner",
    weitereOrdner: "Weitere Ordner",
    menuAria: "Ordner des Postfachs",
    laedt: "Ordner werden geladen …",
    fehler: "Ordner konnten nicht geladen werden.",
    keine: "Keine Ordner vorhanden",
    aufklappen: (name: string) => `${name} aufklappen`,
    zuklappen: (name: string) => `${name} zuklappen`,
    // Die Standard-Ordner, die die Postfach-Suche EINZELN durchsuchen kann.
    // Schlüssel = `MailSearchFolder` (app/clients/email_service.py) und damit
    // der Wert, der beim Server im Pfad landet — nicht übersetzen, nur anzeigen.
    suchbereich: {
      inbox: "Posteingang",
      archive: "Archiv",
      sentitems: "Gesendet",
      drafts: "Entwürfe",
      deleteditems: "Gelöschte Elemente",
      junkemail: "Junk-E-Mail",
      outbox: "Postausgang",
    },
    // Verschieben-Dialog
    verschiebenTitel: "In Ordner verschieben",
    verschiebenBeschreibung:
      "Die Nachricht wird in Outlook in den gewählten Ordner verschoben.",
    filterPlatzhalter: "Ordner filtern …",
    filterAria: "Ordner filtern — die Eingabetaste übernimmt den einzigen Treffer",
    filterOhneTreffer: (begriff: string) => `Kein Ordner passt zu „${begriff}“.`,
    vorschlaegeTitel: "Meist abgelegt in",
    vorschlagGrund: (anzahl: number, domain: string) => `${anzahl}× von ${domain}`,
    verschiebenFehlgeschlagen: "Verschieben fehlgeschlagen",
    verschiebenFehlgeschlagenText: (ziel: string, grund: string) =>
      `Die Nachricht liegt weiter an ihrem Platz (Ziel „${ziel}“). ${grund}`.trim(),
  },

  // ── Löschen: Quittung + Rückgängig ────────────────────────────────────────
  // Graph löscht nicht, es VERSCHIEBT nach „Gelöschte Elemente" — der Toast sagt
  // genau das (und nicht „gelöscht"), sonst sucht der Nutzer im falschen Ordner.
  loeschen: {
    verschoben: "In „Gelöschte Elemente“ verschoben",
    rueckgaengig: "Rückgängig",
    rueckgaengigFehlgeschlagen: "Zurückholen fehlgeschlagen",
    rueckgaengigFehlgeschlagenText: (grund: string) =>
      `Die Nachricht liegt weiter in „Gelöschte Elemente“. ${grund}`.trim(),
  },

  // ── Geöffnete Mail: Kopf, Aktionen, Körper ────────────────────────────────
  lesen: {
    zurueckZurListe: "Zurück zur Liste",
    nichtGefundenTitel: "Mail nicht gefunden",
    nichtGefundenHinweis: "Diese Nachricht existiert nicht mehr im Postfach.",
    nichtErreichbarTitel: "Mail-Service nicht erreichbar",
    nichtErreichbarHinweis: "Die Nachricht konnte nicht geladen werden.",
    keinBetreff: "(kein Betreff)",
    antwortVorschlagen: "Antwort vorschlagen",
    allenAntworten: "Allen antworten",
    freiAntworten: "Frei antworten",
    weiterleiten: "Weiterleiten",
    weitereAktionen: "Weitere Aktionen",
    kontext: "Kontext",
    kontextEinblenden: "Intelligence-Panel einblenden",
    alsUngelesenMarkieren: "Als ungelesen markieren",
    alsGelesenMarkieren: "Als gelesen markieren",
    zustandUngelesen: "Ungelesen",
    zustandGelesen: "Gelesen",
    anhang: "Anhang",
    wichtig: "Wichtig",
    empfaengerAn: "An:",
    empfaengerKopie: "Kopie:",
    weitereEmpfaenger: (n: number) => `+${n} weitere`,
    wenigerEmpfaenger: "weniger",
    archivieren: "Archivieren",
    verschieben: "In Ordner verschieben",
    zusammenfassung: "Zusammenfassung",
    externeBilderAusgeblendet: "Externe Bilder ausgeblendet",
    bildNichtVerfuegbar: "Bild nicht verfügbar",
    keinInhalt: "(kein Inhalt)",
    fruehereNachrichten: "Frühere Nachrichten in dieser Mail",
    downloadFehlgeschlagen: "Download fehlgeschlagen",
  },

  // ── Salesforce-Kontext (Panel rechts) ─────────────────────────────────────
  kontext: {
    reiterKontext: "Kontext",
    reiterFragen: "Fragen",
    panelAusblenden: "Panel ausblenden",
    panelEinblenden: "Kontext & Fragen einblenden",
    breiteZiehen: "Breite ziehen",
    sfKontext: "Salesforce-Kontext",
    threadVerlauf: "Thread-Verlauf",
    eingehend: "eingehend",
    ausgehend: "ausgehend",
    account: (name: string) => `Account: ${name}`,
    dringlichkeit: (wert: string) => `Dringlichkeit: ${wert}`,
    verknuepfteDatensaetze: "Verknüpfte Datensätze",
    // Die drei Nicht-ready-Zustände der Anreicherung
    missPending:
      "Salesforce-Kontext gerade nicht verfügbar — beim Aktualisieren wird es erneut versucht.",
    missIntern: "Interne Mail — kein Kundenkontext in Salesforce.",
    missKeinTreffer: "Zu diesem Absender gibt es keinen Salesforce-Datensatz.",
    modulFehler: "Salesforce-Daten für dieses Modul gerade nicht verfügbar.",
    linksDegradiert:
      "Verknüpfte Datensätze gerade nicht verfügbar — Salesforce hat nicht geantwortet.",
    keineLinks: "Keine verknüpften Datensätze gefunden.",
    inSfOeffnen: (name: string) => `${name} — in Salesforce öffnen`,
    konfidenz: "Trefferwahrscheinlichkeit der Verknüpfung",
    // Überschriften der Verknüpfungs-Gruppen (Schlüssel = SF-Objektname)
    gruppe: {
      Batch_Record: "Batch Records",
      Registration: "Registrierungen",
      Sales_Position: "Verkaufspositionen",
      Artwork: "Artworks",
      Formulation_Group: "Formulierungsgruppen",
    },
    // Sachfelder der Verknüpfungs-Karten (Schlüssel = Feldname im Kontrakt)
    feld: {
      batch_no: "Charge",
      manufacturing_date: "Herstellung",
      expiry_date: "Verfall",
      country: "Land",
      type: "Typ",
      launch_date: "Launch",
      quantity: "Menge",
      value: "Wert",
      expected_delivery_date: "Liefertermin",
      phase: "Phase",
      valid_from: "Gültig ab",
    },
    // Verkaufs-Stufen (Schlüssel = SF-Rohwert)
    stufe: {
      "Waiting for Customer FC": "Wartet auf Forecast",
      "Waiting for order": "Wartet auf PO",
      "Approval process": "Freigabe-Prozess",
      "Receiving Confirmation from CMO": "CMO-Bestätigung",
      "Under Production": "In Produktion",
      Processing: "In Bearbeitung",
      "Releasing goods": "Warenfreigabe",
    },
    // Account-Karte + Kennzahlen-Module
    account_: "Account",
    absender: "Absender",
    ohneZuordnung: "Ohne Zuordnung",
    unbekannterAbsender: "Unbekannter Absender",
    lead: "Lead",
    funktionNichtHinterlegt: "Funktion nicht hinterlegt",
    keinAccount: "Kein Account in Salesforce verknüpft — keine Kennzahlen.",
    accountFehlerReferenz: "Ungültige Account-Referenz.",
    accountFehlerLaden: "Account-Kontext konnte nicht geladen werden.",
    erneut: "Erneut",
    keineModule: "Keine Module ausgewählt.",
    keineDaten: "Keine Daten.",
    keineUmsatzdaten: "Keine Umsatzdaten.",
    gesamt: "Gesamt:",
    inklAuftragsbestand: (jahr: number) => `inkl. Auftragsbestand (ab ${jahr})`,
    istUmsatz: "Ist-Umsatz",
    balkenTitel: (jahr: number, umsatz: string, auftraege: number) =>
      `${jahr}: ${umsatz} · ${auftraege} Aufträge`,
    keineOffenenBestellungen: "Keine offenen Bestellungen.",
    keineAuftraegeAbwicklung: "Keine Aufträge in Abwicklung.",
    keineRegistrierungen: "Keine laufenden Registrierungen.",
    wenigerAnzeigen: "Weniger anzeigen",
    alleAnzeigen: (n: number) => `Alle ${n} anzeigen`,
    launch: "Launch",
    launchTitel: (datum: string) => `Launch: ${datum}`,
    modul: {
      revenue_by_year: {
        label: "Umsatzentwicklung",
        beschreibung: "Umsatz pro Jahr inkl. Auftragsbestand.",
      },
      open_orders: {
        label: "Offene Bestellungen (Warten auf Kunde)",
        beschreibung: "Aufträge, die auf Forecast oder Bestellung warten.",
      },
      processing_orders: {
        label: "In Abwicklung",
        beschreibung: "Aufträge in Produktion, Freigabe oder Bearbeitung.",
      },
      registrations: {
        label: "Registrierungen in Arbeit",
        beschreibung: "Laufende Zulassungen je Land.",
      },
    },
    panelKonfigurieren: "Panel konfigurieren",
    panelKonfigurierenBeschreibung:
      "Welche Module im Account-Kontext angezeigt werden.",
    panelStandardHinweisA: "Ohne eigene Auswahl gilt der ",
    panelStandardHinweisB: "Standard deiner Abteilung",
    panelStandardHinweisC: ".",
    panelGespeichert: "Panel-Konfiguration gespeichert",
    panelNichtGespeichert: "Konnte serverseitig nicht speichern",
  },

  // ── Verfassen-Fenster (Antworten · Allen · Weiterleiten · Neue Mail) ──────
  composer: {
    kopfEntwurf: "Entwurf",
    kopfNeueMail: "Neue Mail",
    kopfAllenAntworten: "Allen antworten",
    kopfWeiterleiten: "Weiterleiten",
    kopfKiVorschlaege: "KI-Antwortvorschläge",
    kopfFreiAntworten: "Frei antworten",
    vorschlaegeLaden: "Vorschläge werden erstellt … (bis zu 20 s)",
    fehlerDienst: "Der Mail-Service ist gerade nicht erreichbar.",
    fehlerGenerierung: "Die Antwort-Generierung ist fehlgeschlagen.",
    fehlerAllgemein: "Vorschläge konnten nicht erstellt werden.",
    varianteBearbeiten: "Diese Variante bearbeiten",
    zurueckZuVarianten: "Zurück zu den Varianten",
    vorlagen: (n: number) => `Vorlagen: ${n}`,
    ectoinWissen: "Ectoin-Wissen",
    ectoinQuelle: "Ectoin-Quelle",
    an: "An",
    anAlle: "An alle (editierbar)",
    anPlatzhalter: "empfaenger@example.com",
    cc: "Cc",
    ccHinweis: "optional, mehrere mit Komma trennen",
    optional: "optional",
    bcc: "Bcc",
    bccHinweis: "optional — Empfänger sehen sich gegenseitig nicht",
    bccHinzufuegen: "Bcc hinzufügen",
    betreff: "Betreff",
    nachricht: "Nachricht",
    nachrichtPlatzhalter: "Nachricht verfassen …",
    notiz: "Notiz",
    notizOptional: "Notiz (optional)",
    notizPlatzhalter: "Optionale Notiz zum Weiterleiten …",
    signatur: (name: string) => `Signatur${name ? ` (${name})` : ""} — `,
    signaturSchonImText:
      "steht schon in deinem Text, wird NICHT noch einmal angehängt",
    signaturWirdAngehaengt: "wird beim Senden angehängt",
    signaturAbgeschaltet: "für diese Mail abgeschaltet — wird NICHT angehängt",
    signaturSchalterAria: "Signatur an diese Mail anhängen",
    signaturSchalterTitel:
      "Gilt nur für diese Mail. Das nächste Verfassen-Fenster hängt sie wieder an.",
    ausDeinemPostfach: "Gesendet wird direkt aus deinem Postfach.",
    entwurfSpeichert: "Entwurf speichert …",
    entwurfGespeichert: (zeit: string) => `Entwurf gespeichert · ${zeit}`,
    wirdGesendet: "Wird gesendet …",
    rueckgaengig: (sekunden: number) => `Rückgängig (${sekunden})`,
    senden: "Senden",
    rueckholzeit: "Rückholzeit",
    rueckholzeitAria: "Rückholzeit nach dem Senden",
    // Die Auswahl selbst. `ui/undoSendPref.ts` hält nur noch die WERTE
    // (0/5/7/10) — der Text steht hier, sonst stünde er in der englischen
    // Oberfläche deutsch neben seiner übersetzten Beschriftung.
    rueckholzeitOption: (sekunden: number) =>
      sekunden === 0 ? "Sofort senden" : `${sekunden} Sek. Rückholzeit`,
    // Rückmeldungen des Versands
    keinEmpfaenger: "Kein Empfänger",
    keinEmpfaengerText: "Bitte mindestens eine Empfänger-Adresse angeben.",
    weitergeleitet: "Weitergeleitet",
    gesendet: "Gesendet",
    gesendetAn: (empfaenger: string) => `Aus deinem Postfach an ${empfaenger}.`,
    gesendetAnVerteiler: "Aus deinem Postfach an den Verteiler.",
    sendenFehlgeschlagen: "Senden fehlgeschlagen",
    sendenFehlerAnhangSync:
      "Die Anhänge des Entwurfs konnten nicht aktualisiert werden — es wurde nichts gesendet. Bitte erneut versuchen.",
    sendenFehler403: "Dein Postfach ist (noch) nicht sendeberechtigt.",
    sendenFehler413:
      "Die Anhänge sind zusammen zu groß (max. rund 2,5 MB je Mail). Bitte eine Datei entfernen oder als Link teilen.",
    sendenFehler404: "Die Bezugsnachricht ist nicht mehr im Postfach.",
    sendenFehler502: "Der Mail-Dienst konnte die Nachricht nicht senden.",
    sendenFehlerSonst: "Bitte später erneut versuchen.",
    // Rich-Text-Werkzeugleiste
    fett: "Fett",
    fettTitel: "Fett (Strg+B)",
    kursiv: "Kursiv",
    kursivTitel: "Kursiv (Strg+I)",
    aufzaehlung: "Aufzählung",
    linkEinfuegen: "Link einfügen",
    linkEinfuegenTitel: "Link einfügen (Strg+K)",
    linkOk: "OK",
    // Formulierhilfe
    assistHinweisA: "KI: ",
    assistHinweisB: " weiterschreiben · ",
    assistHinweisC: " Absatz",
    assistHinweisText: "Text",
    assistHinweisAnweisung: "<Anweisung>>>",
    assistLaeuftGefuehrt: "Formulierhilfe schreibt den Absatz …",
    assistLaeuftFrei: "Formulierhilfe schreibt weiter …",
    assistDauer: "Das dauert ein paar Sekunden.",
    assistFertig: "Formulierhilfe eingefügt.",
    assistGebremst: (zeichen: number) =>
      `Wiederholung entfernt (${zeichen} Zeichen).`,
    assistFehlerTitel: "Formulierhilfe hat nicht geklappt.",
    assistFehlerNachsatz: "Deine Anweisung steht noch im Entwurf.",
    assistNochmal: "Nochmal",
    assistGrund502: "Das Modell hat keinen verwertbaren Text geliefert.",
    assistGrund503: "Der Mail-Dienst ist gerade nicht erreichbar.",
    assistGrund403: "Dieses Postfach ist nicht freigeschaltet.",
    assistGrund404: "Die Bezugsnachricht gibt es nicht mehr.",
    assistGrundOffline: "Keine Verbindung zum Server.",
    assistGrundStatus: (status: number) => `Der Server antwortete mit ${status}.`,
    assistGrundUnbekannt: "Unbekannter Fehler.",
    assistLeer: "Das Modell hat nichts geliefert.",
    assistNurWiederholt: "Das Modell hat nur wiederholt, was schon dasteht.",
    assistStelleWeg: "Die Stelle im Entwurf ist nicht mehr auffindbar.",
  },

  // ── Anhänge: anhängen, lesen, Vorschau ────────────────────────────────────
  anhaenge: {
    ablegen: "Datei hier ablegen zum Anhängen",
    dateiAnhaengen: "Datei anhängen",
    anhang: "Anhang",
    entfernen: "Anhang entfernen",
    leseFehler: "Anhang fehlgeschlagen",
    leseFehlerText: (name: string) => `„${name}" konnte nicht gelesen werden.`,
    zuGross: (dateien: string[]) =>
      `${dateien.map((n) => `„${n}"`).join(", ")} ${
        dateien.length > 1 ? "sind" : "ist"
      } zu groß und wurde${dateien.length > 1 ? "n" : ""} nicht angehängt. ` +
      "Alle Anhänge einer Mail dürfen zusammen rund 2,5 MB nicht " +
      "überschreiten — größere Dateien bitte als Link teilen.",
    entwurfNichtAngelegt:
      "Der Entwurf konnte nicht angelegt werden — die Anhänge sind noch nicht zwischengespeichert. Beim Senden gehen sie trotzdem mit.",
    syncZuGross:
      "Die Anhänge sind zusammen zu groß für den Entwurf (max. rund 2,5 MB je Mail). Bitte eine Datei entfernen.",
    syncFehler:
      "Der Anhang konnte nicht im Entwurf zwischengespeichert werden. Er bleibt hier und geht beim Senden mit.",
    entfernenFehler:
      "Der Anhang konnte nicht aus dem zwischengespeicherten Entwurf entfernt werden. Er wird vor dem Senden erneut entfernt.",
    // Leiste + Vorschau der geöffneten Mail
    seiten: (n: number) => `${n} Seiten`,
    ansehen: (name: string) => `${name} ansehen`,
    keineVorschauTitel: (name: string) =>
      `${name} — für dieses Format gibt es keine Vorschau, nur den Download`,
    herunterladenTitel: (name: string) => `${name} herunterladen`,
    anlageHerunterladen: "Anlage herunterladen",
    // Sammel-Download: n EINZELNE Dateien, kein Archiv. Der Titel sagt das
    // ausdrücklich — sonst rechnet der Nutzer mit einem ZIP und sucht danach.
    alleHerunterladen: (n: number) => `Alle herunterladen (${n})`,
    alleHerunterladenTitel: (n: number) =>
      `Alle ${n} Anlagen als einzelne Dateien herunterladen (kein ZIP)`,
    alleHerunterladenLaeuft: "Anlagen werden geladen …",
    alleFehlgeschlagen: "Anlagen konnten nicht geladen werden",
    alleFehlgeschlagenText:
      "Die Anlagen lassen sich gerade nicht am Stück laden. Einzeln geht es weiterhin.",
    alleZuGross:
      "Die Anlagen sind zusammen zu groß für einen Sammel-Download. Bitte einzeln herunterladen.",
    vorschauSchliessen: "Vorschau schließen",
    vorschauTitel: (name: string) => `Vorschau ${name}`,
    laedt: "Anlage wird geladen …",
    fehler404: "Diese Anlage ist nicht mehr abrufbar.",
    fehler403: "Für dieses Postfach fehlt die Freigabe.",
    fehlerStatus: (status: number) =>
      `Die Anlage konnte nicht geladen werden (Fehler ${status}).`,
    fehlerAllgemein: "Die Anlage konnte nicht geladen werden.",
    keineVorschau: "Für dieses Format gibt es keine Vorschau.",
  },

  // ── Entwürfe-Ansicht ──────────────────────────────────────────────────────
  entwuerfe: {
    entwurf: "Entwurf",
    keinEmpfaenger: "(kein Empfänger)",
    keinBetreff: "(Kein Betreff)",
    enthaeltAnhaenge: "Enthält Anhänge",
    loeschen: "Entwurf löschen",
    loeschenFehlgeschlagen: "Löschen fehlgeschlagen",
    leerTitel: "Keine Entwürfe",
    leerHinweis:
      "Sobald du eine Mail zu verfassen beginnst, wird sie hier als Entwurf gespeichert.",
  },

  // ── Vorlagen (Auswahl im Composer + eigener Reiter) ───────────────────────
  vorlagen: {
    einfuegen: "Vorlage einfügen",
    wirdAufgeloest: "Wird aufgelöst …",
    laedt: "lädt …",
    keine: "Keine Vorlagen.",
    datensatzWaehlen: (name: string) => `„${name}" — Datensatz wählen`,
    ohneDatensatz: "Ohne Datensatz",
    aufloesenFehlgeschlagen: "Auflösen fehlgeschlagen",
    aufloesenNichtGefunden: "Vorlage nicht gefunden.",
    aufloesenErneut: "Bitte erneut versuchen.",
    eingefuegt: (name: string) => `Vorlage „${name}" eingefügt`,
    felderGefuellt: (n: number) => `${n} Felder gefüllt`,
    offen: (n: number) => `${n} offen`,
    anhang: (n: number) => `${n} Anhang`,
    sfNichtErreichbar: "SF nicht erreichbar",
    // Reiter „Vorlagen"
    reiterHinweis: "Abteilungs-Vorlagen als Grundlage für KI-Antworten.",
    neu: "Neu",
    ladefehlerTitel: "Vorlagen konnten nicht geladen werden",
    ladefehlerHinweis: "Versuch es in einem Moment erneut.",
    leerTitel: "Noch keine Vorlagen",
    leerHinweis:
      "Lege deine erste Abteilungs-Vorlage an — sie fließt danach als Baukasten in die KI-Antworten ein.",
    leerAktion: "Vorlage anlegen",
    seed: "Seed",
    manuell: "Manuell",
    deaktivieren: "Deaktivieren",
    wirklichDeaktivieren: "Wirklich deaktivieren?",
    deaktiviert: "Vorlage deaktiviert",
    deaktivierenFehlgeschlagen: "Deaktivieren fehlgeschlagen",
    modalBearbeiten: "Vorlage bearbeiten",
    modalNeu: "Neue Vorlage",
    modalBeschreibung: "Abteilungs-Vorlage als Baukasten für KI-Antworten.",
    gespeichert: "Vorlage gespeichert",
    angelegt: "Vorlage angelegt",
    kategorie: "Kategorie",
    kategorieHinweis: "z. B. Quality, Sales, Regulatory",
    kategoriePlatzhalter: "Quality",
    subkategorie: "Subkategorie",
    subkategoriePlatzhalter: "Amendment",
    name: "Name",
    namePlatzhalter: "Amendment-Eingangsbestätigung",
    text: "Text",
    textHinweis: "Markdown erlaubt; [Name] als Signatur-Platzhalter.",
    textPlatzhalter: "Sehr geehrte Damen und Herren, …",
    notizen: "Notizen",
    notizenHinweis: "Wann diese Vorlage passt (nur intern).",
  },

  // ── Reiter „Fragen" (Salesforce in natürlicher Sprache) ───────────────────
  fragen: {
    beta: "Beta",
    betaHinweis: " — die Abfrage-Engine wird noch validiert; Ergebnisse bitte prüfen.",
    leerTitel: "Frag Salesforce in natürlicher Sprache",
    leerHinweis:
      "Stelle eine Frage zu Accounts, Umsätzen oder Registrierungen — die Antwort kommt als Tabelle.",
    beispiele: [
      "Wie viele Accounts gibt es?",
      "Umsatz von Opella 2025",
      "Offene Registrierungen für Polen",
    ],
    platzhalter: "Frag Salesforce … (z. B. Umsatz von Opella 2025)",
    fragen: "Fragen",
    denktNach: "Salesforce denkt nach …",
    keineTreffer: "Keine Treffer.",
    treffer: (n: number) => `${n} Treffer`,
    nurErste50: "nur erste 50 gezeigt",
    details: "Details",
    objekt: (name: string) => `Objekt: ${name}`,
    konfidenz: (prozent: number) => `Konfidenz: ${prozent}%`,
    soqlKopiert: "SOQL kopiert",
    soqlKopierenFehler: "Kopieren nicht möglich",
    soqlKopierenAria: "SOQL kopieren",
    kopieren: "Kopieren",
    wert: "Wert",
    wertN: (n: number) => `Wert ${n}`,
    zeitueberschreitung: "Zeitüberschreitung — die Abfrage dauerte zu lange.",
    engineWeg: "Die Abfrage-Engine ist gerade nicht erreichbar.",
    nichtBeantwortbar: "Frage nicht beantwortbar.",
    konkreter:
      "Formuliere die Frage konkreter (Objekt, Zeitraum, Land) und versuch es erneut.",
    stattdessenFragen: (frage: string) => `Stattdessen fragen: ${frage}`,
    abstainUnanswerable:
      "Diese Frage lässt sich nicht aus den Salesforce-Daten beantworten.",
    abstainKeinObjekt:
      "Ich konnte die Frage keinem Datenobjekt zuordnen. Formuliere sie konkreter (Objekt, Zeitraum, Land).",
    abstainUngueltig:
      "Die erzeugte Abfrage war nicht gültig. Formuliere die Frage bitte anders.",
    abstainTimeout: "Die Abfrage-Engine hat zu lange gebraucht. Bitte versuch es erneut.",
    abstainSonst: "Dazu kann ich keine belastbare Antwort geben.",
  },

  // ── Nachverfolgung (Follow-up / Chasing) ──────────────────────────────────
  nachfass: {
    reiterHinweis:
      "Markierte Mails fassen automatisch nach: freundlich → schärfer → deutlich, dann ein Teams-Eskalationsmeeting. Hier anpassen oder pausieren.",
    ladefehlerTitel: "Nachverfolgungen konnten nicht geladen werden",
    ladefehlerHinweis: "Versuch es in einem Moment erneut.",
    leerTitel: "Noch keine Nachverfolgungen",
    leerHinweis:
      "Markiere eine Mail im Posteingang mit dem Wecker-Symbol — sie läuft dann sofort automatisch.",
    keinBetreff: "(kein Betreff)",
    unbekannt: "Unbekannt",
    // Zustands-Beschriftungen (Schlüssel = Zustand im Kontrakt)
    zustand: {
      draft: "Entwurf",
      armed: "scharf",
      paused: "pausiert",
      answered: "beantwortet",
      stopped: "gestoppt",
      needs_decision: "Entscheidung noetig",
    },
    antwort: {
      none: "keine Antwort",
      holding: "abwartend",
      real: "echte Antwort",
      ooo: "Abwesenheit",
    },
    // Entscheidungs-Korb
    entscheidungZaehler: (n: number) => `${n} Vorgänge brauchen eine Entscheidung`,
    entscheidungTitel: "Braucht deine Entscheidung",
    entscheidungHinweis:
      "Auf diese Vorgänge kam eine Antwort, die weder eine Abwesenheit noch eine Terminzusage war — oder die Nachfass-Stufen sind ausgereizt und Atlas fragt, ob ein Eskalationsmeeting angesetzt werden soll. Sie sind angehalten: nichts geht raus und es entsteht kein Termin, bis du entschieden hast.",
    grundEchteAntwort:
      "Es kam eine inhaltliche Antwort — abgeschlossen oder weiter dranbleiben?",
    grundUnzuordenbar:
      "Eine Mail im Vorgang liess sich nicht zuordnen — bitte kurz ansehen.",
    grundDatumUnklar:
      "Die Antwort nennt einen Termin, der sich nicht sicher lesen liess.",
    grundEskalation:
      "Die Nachfass-Stufen sind ausgereizt. Soll Atlas jetzt in deinem Namen ein Eskalationsmeeting ansetzen und die Eskalationsmail schicken?",
    grundSonst: "Es kam eine Antwort, die eine Entscheidung braucht.",
    terminAnlegen: "Termin anlegen",
    erledigt: "Erledigt",
    keineEskalation: "Keine Eskalation",
    weiterNachfassen: "Weiter nachfassen",
    inMailOeffnen: "In Mail öffnen",
    vorgeschlagenerTermin: "Vorgeschlagener Termin",
    eingeladen: (n: number) => `Eingeladen (${n})`,
    antwortUnbekannt: "(unbekannt)",
    antwortWeg:
      "Mail nicht mehr auffindbar — sie wurde endgültig gelöscht. Einstufung und Begründung oben bleiben erhalten.",
    antwortFehler: "Die Mail konnte nicht geladen werden.",
    // Zeile
    laeuftPausieren: "Läuft – pausieren",
    pausiertFortsetzen: "Pausiert – fortsetzen",
    erstScharfschalten: "Erst scharfschalten",
    laeuftKlickPausieren: "Läuft – klicken zum Pausieren",
    pausiertKlickFortsetzen: "Pausiert – klicken zum Fortsetzen",
    naechsterSchrittEskalation: "Nächster Schritt: Eskalation",
    naechsteNachfrage: "Nächste Nachfrage",
    eskalationPraefix: "Eskalation ",
    naechstePraefix: "nächste ",
    spracheHaus:
      "Sprache der Nachfass-Mails — Deutsch, weil im Verteiler nur bitop-Adressen stehen",
    spracheErkannt: "Sprache der Nachfass-Mails — aus der Konversation erkannt",
    empfaengerFehlt: "Empfänger (An) fehlt — im Anpassen-Dialog ergänzen",
    internesGegenueber:
      "Gegenüber ist bitop-intern — läuft erst nach deiner Bestätigung an",
    scharf: "Scharf",
    anpassen: "Anpassen",
    // Aktionen + Meldungen
    scharfgeschaltet: "Scharfgeschaltet",
    scharfgeschaltetText:
      "Fahrplan berechnet — Reminder & Eskalation laufen ab jetzt automatisch.",
    scharfschaltenFehler: "Scharfschalten nicht möglich",
    gestoppt: "Gestoppt",
    reaktiviert: "Reaktiviert",
    aktionFehlgeschlagen: "Aktion fehlgeschlagen",
    loeschenTitel: "Nachverfolgung löschen?",
    loeschenBeschreibung: (betreff: string) =>
      `„${betreff}" wird samt Fahrplan entfernt.`,
    geloescht: "Gelöscht",
    loeschenFehlgeschlagen: "Löschen fehlgeschlagen",
    entscheidungFehlgeschlagen: "Entscheidung fehlgeschlagen",
    meldungDoneTitel: "Als erledigt abgelegt",
    meldungDoneText: "Der Vorgang taucht nicht mehr in der Entscheidungsliste auf.",
    meldungContinueTitel: "Nachverfolgung läuft weiter",
    meldungContinueText: "Der Fahrplan startet ab heute neu.",
    meldungEscalateTitel: "Eskalationsmeeting angelegt",
    meldungEscalateText: (termin: string) =>
      `${termin} — die Einladung und die Eskalationsmail sind raus.`,
    meldungEscalateFallback: "Der Termin",
    meldungNoEscalateTitel: "Keine Eskalation",
    meldungNoEscalateText:
      "Es wurde kein Termin angelegt und keine Mail verschickt. Der Vorgang ist gestoppt.",
    // Markieren aus der Liste
    markiertLaeuftTitel: "Nachverfolgung läuft",
    markiertLaeuftText: (datum: string) =>
      `Erste Nachfrage am ${datum} · im Tab Nachverfolgung anpassbar.`,
    markiertLaeuftOhneDatum: "Läuft automatisch · im Tab Nachverfolgung anpassbar.",
    markiertTitel: "Zur Nachverfolgung markiert",
    markiertInternText:
      "Gegenüber ist bitop-intern — im Tab Nachverfolgung prüfen + scharfschalten.",
    markiertOffenText:
      "Empfänger fehlt — im Tab Nachverfolgung ergänzen + scharfschalten.",
    markierenFehlgeschlagen: "Markieren fehlgeschlagen",
    // Was die Engine selbst getan hat (Transparenz-Paket 20.08.2026).
    // Anlass: die Engine hat auf eine Datums-Zusage eingerastet, in Vertretung
    // eine Bestätigung verschickt und einen fälligen Reminder übersprungen —
    // und NICHTS davon gezeigt. Der Tester las daraus ein kaputtes Intervall.
    abwartendSeit: (datum: string) => `abwartend seit ${datum}`,
    chipZusage: "Zusage",
    chipZusageTitel: (datum: string) =>
      `Die Gegenseite hat den ${datum} zugesagt — der Fahrplan ist darauf eingerastet.`,
    eingerastet: (ziel: string, quelle: string) =>
      `auf den ${ziel} eingerastet — Zusage aus der Antwort vom ${quelle}`,
    eingerastetOhneQuelle: (ziel: string) =>
      `auf den ${ziel} eingerastet — Zusage aus der Antwort`,
    bestaetigungVerschickt: (datum: string) =>
      `Bestätigung am ${datum} in deinem Namen verschickt`,
    bestaetigungStattNachfrage: (datum: string) =>
      `Bestätigung am ${datum} in deinem Namen verschickt — die fällige Nachfrage entfiel dadurch`,
    nachfrageUebersprungen: (datum: string) =>
      `Nachfrage vom ${datum} übersprungen — die Bestätigung hat sie überholt`,
    uebersprungenGrund: " · übersprungen: die Bestätigung hat diese Nachfrage überholt",
    nachfassZaehler: (n: number) => `${n} Vorgänge brauchen deinen Blick`,
    // Fahrplan
    fahrplanStart: "Start",
    fahrplanEskalation: "Esk.",
    fahrplanReminder: (n: number) => `R${n}`,
    // Anpassen-Dialog
    konfigurationGespeichert: "Konfiguration gespeichert",
    nichtGespeichert: "Nicht gespeichert",
    unbekannterFehler: "Unbekannter Fehler",
    feldAbgelehnt: (wert: string) => `„${wert}“ wurde abgelehnt`,
    feldLabel: {
      to: "An (Empfänger)",
      cc: "CC",
      bitop_attendees: "bitop-Teilnehmer",
      partner_attendees: "Partner-Teilnehmer",
    },
    betreffPraefix: (betreff: string) => `Betreff: ${betreff}`,
    nochNichtScharf: "Noch nicht scharfgeschaltet — Anker = heute (Vorschau).",
    eskaliert: (gesendet: number, gesamt: number) =>
      `Eskaliert · ${gesendet}/${gesamt} Nachfragen gesendet.`,
    nachfragenGesendet: (gesendet: number, gesamt: number) =>
      `${gesendet}/${gesamt} Nachfragen gesendet`,
    naechsteAm: (was: string, datum: string) => ` · ${was} am ${datum}`,
    wortEskalation: "Eskalation",
    wortNaechste: "nächste",
    tonLadder: ". Ton: freundlich → schärfer → deutlich, dann Meeting.",
    intervall: "Intervall (Tage)",
    intervallHinweis: "Abstand zwischen den Nachfragen.",
    anzahlNachfragen: "Anzahl Nachfragen",
    anzahlNachfragenHinweis: "Nachfragen vor der Eskalation.",
    neuerFahrplan: "Neuer Fahrplan nach dem Speichern",
    an: "An (Empfänger)",
    anPlatzhalter: "Name tippen oder mail@kunde.de",
    cc: "CC",
    ccPlatzhalter: "Name tippen oder cc@…",
    verteilerAlle: "Alle Nachfragen an den ganzen Verteiler",
    verteilerAlleAn: "Reminder + Eskalation gehen an An + CC (wie die Ursprungsmail).",
    verteilerAlleAus:
      "Sanfte Reminder nur an den Hauptempfänger; erst die Eskalation an alle.",
    eskalationsMeeting: "Eskalations-Meeting (Teams) · ",
    eingeladenAnzahl: (n: number) => `${n} eingeladen`,
    eskalationHinweis:
      "Du organisierst; der ganze Verteiler wird automatisch eingeladen — einzelne per Klick entfernen (wieder hinzufügen möglich). Betrifft nur das Meeting, nicht die Nachfass-Mails.",
    keinVerteiler: "Kein Verteiler erfasst — bei der Eskalation geht nur die Mail raus.",
    wiederEinladen: "Wieder einladen",
    ausEinladungEntfernen: "Aus Einladung entfernen",
    bitopTeilnehmer: "bitop-Teilnehmer",
    bitopTeilnehmerPlatzhalter: "Name tippen oder person@bitop.de",
    partnerTeilnehmer: "Partner-Teilnehmer",
    partnerTeilnehmerPlatzhalter: "Name tippen oder person@partner.de",
    notiz: "Notiz",
    notizPlatzhalter: "Kontext / Agenda fürs Eskalations-Meeting …",
    optional: "optional",
    // Chips-Eingabe
    chipEntfernen: (adresse: string) => `${adresse} entfernen`,
    chipAria: (feld: string) => `${feld} — Name oder E-Mail tippen`,
    chipHinweisA: "↑↓",
    chipHinweisB: " auswählen · ",
    chipHinweisC: "Enter",
    chipHinweisD: " übernimmt",
    chipSucht: "Wird gesucht …",
    chipKeinVorschlag:
      "Kein Vorschlag — vollständige Adresse tippen, Enter übernimmt sie.",
    extern: "Extern",
  },

  // ── Termin-Einladung aus der Mail beantworten ─────────────────────────────
  termin: {
    ueberschrift: "Termin-Einladung — Antwort an den Organisator",
    zusagen: "Zusagen",
    mitVorbehalt: "Mit Vorbehalt",
    absagen: "Absagen",
    antwortVerschickt: "Antwort verschickt",
    antwortFehlgeschlagen: "Antwort fehlgeschlagen",
    fehlerDienst: "Kalender-Dienst nicht erreichbar.",
    fehlerFreigabe: "Für diesen Kalender fehlt die Freigabe.",
    status: (wert: string) => `Status: ${wert}`,
    label: {
      accept: "Zusage verschickt",
      tentativelyAccept: "Mit Vorbehalt zugesagt",
      decline: "Absage verschickt",
    },
  },

  // ── Abwesenheitsnotiz ─────────────────────────────────────────────────────
  abwesenheit: {
    titel: "Abwesenheitsnotiz",
    laedt: "Lädt …",
    nichtFreigeschaltet:
      "Dieses Postfach ist nicht freigeschaltet. Die Abwesenheitsnotiz steht nur für synchronisierte Postfächer zur Verfügung.",
    nichtVerfuegbar:
      "Abwesenheitsnotiz derzeit nicht verfügbar (Postfach-Einstellungen noch nicht freigeschaltet). Speichern ist deaktiviert.",
    automatischeAntwort: "Automatische Antwort",
    aktiv: "aktiv — eingehende Mails werden automatisch beantwortet",
    aus: "aus",
    aktivierenAria: "Abwesenheitsnotiz aktivieren",
    internerText: "Interner Antworttext",
    internerTextHinweis: "Geht an Absender innerhalb der Organisation.",
    internerTextPlatzhalter: "Ich bin derzeit nicht im Büro …",
    zeitraumFestlegen: "Zeitraum festlegen",
    zeitraumAktiv: "nur zwischen Von und Bis aktiv",
    zeitraumInaktiv: "aktiv bis manuell ausgeschaltet",
    zeitraumUnvollstaendigA: "Zeitraum unvollständig — ",
    zeitraumBeideFehlen: "Von und Bis fehlen",
    zeitraumVonFehlt: "das Von-Datum fehlt",
    zeitraumBisFehlt: "das Bis-Datum fehlt",
    zeitraumUnvollstaendigB:
      '. Trag die Daten ein oder schalte „Zeitraum festlegen" aus; ohne Zeitraum lässt sich die Abwesenheitsnotiz nicht begrenzen.',
    vonDatum: "Von (Datum)",
    vonUhrzeit: "Von (Uhrzeit)",
    bisDatum: "Bis (Datum)",
    bisUhrzeit: "Bis (Uhrzeit)",
    datumFormat: "Bitte als TT.MM.JJJJ",
    datumFehlt: "Datum fehlt",
    vonDatumAria: "Abwesend von, Datum (TT.MM.JJJJ)",
    vonUhrzeitAria: "Abwesend von, Uhrzeit",
    bisDatumAria: "Abwesend bis, Datum (TT.MM.JJJJ)",
    bisUhrzeitAria: "Abwesend bis, Uhrzeit",
    externeEmpfaenger: "Antwort an Empfänger außerhalb der Organisation",
    externKeine: "Keine externen Antworten",
    externKontakte: "Nur meine Kontakte",
    externAlle: "Alle externen Absender",
    externerText: "Externer Antworttext",
    externerTextHinweis: "Geht an Absender außerhalb der Organisation.",
    externerTextPlatzhalter: "I am currently out of office …",
    aktiviert: "Abwesenheitsnotiz aktiviert",
    deaktiviert: "Abwesenheitsnotiz deaktiviert",
    fehler403:
      "Postfach nicht freigeschaltet — die Abwesenheitsnotiz kann nicht gesetzt werden.",
    fehlerSonst:
      "Speichern fehlgeschlagen (Postfach-Einstellungen derzeit nicht verfügbar). Bitte später erneut versuchen.",
  },

  // ── Tastenkürzel: Übersicht + Erst-Hinweise ───────────────────────────────
  kuerzel: {
    titel: "Tastenkürzel",
    beschreibung: "Outlook-nativ — an Office angelehnt, kein Gmail-Kürzel.",
    // Was die Taste TUT (die Taste selbst steht in shortcuts.ts::keys)
    aktion: {
      delete: "Nachricht löschen",
      space: "Weiterblättern — am Ende zur nächsten ungelesenen Mail",
      palette: "Befehls-Palette öffnen",
      search: "Suche fokussieren",
      termin: "Termin aus dieser Mail",
      help: "Tastenkürzel-Übersicht",
    },
    notizPalette: "im Verfassen-Editor: Strg+Umschalt+P",
    hinweisSchliessen: "Hinweis schließen",
    erstHinweisNeu: "Neu: ",
    erstHinweisPalette: " = Befehls-Palette · ",
    erstHinweisUebersicht: " = alle Tastenkürzel",
    rechtsklickNeu: "Neu: ",
    rechtsklickWort: "Rechtsklick",
    rechtsklickText:
      " auf eine Mail öffnet alle Aktionen — Verschieben, Nachverfolgen, Antworten. Mit der Tastatur: ",
    rechtsklickTaste: "Umschalt+F10",
  },

  // ── Web-Push-Benachrichtigungen ───────────────────────────────────────────
  benachrichtigung: {
    nichtVerfuegbar: "Benachrichtigungen nicht verfügbar",
    nichtVerfuegbarText:
      "Dieser Browser unterstützt keine Web-Push-Benachrichtigungen.",
    aktiv: "Benachrichtigungen aktiv",
    aktivText: "Du bekommst jetzt einen Hinweis bei neuen, zugeordneten Mails.",
    aktivierungFehlgeschlagen: "Aktivierung fehlgeschlagen",
    bitteErneut: "Bitte erneut versuchen.",
    imBrowserDeaktiviert: "Benachrichtigungen im Browser deaktiviert",
    imBrowserDeaktiviertTitel:
      "In den Browser-Einstellungen wieder erlauben, um Benachrichtigungen zu erhalten.",
    aktivieren: "Benachrichtigungen aktivieren",
  },

  // ── Kontakt / Lead aus einer Mail anlegen ─────────────────────────────────
  kontakte: {
    herkunft: {
      signature: "aus Signatur",
      account: "aus Account",
      sender: "aus Absender",
    },
    geaendert: "geändert",
    erforderlich: "erforderlich",
    leer: "leer",
    mobilE164Hinweis:
      "Mobilnummer muss im internationalen E.164-Format stehen: + Ländervorwahl, dann die Nummer, ohne Leerzeichen und Klammern (z. B. +491701234567). Die nationale Vorwahl-Null entfällt.",
    // Kontakt
    kontaktEinstieg: "Dieser Absender ist noch kein Kontakt in Salesforce.",
    kontaktAnlegen: "Kontakt anlegen",
    kontaktModalTitel: "Kontakt in Salesforce anlegen",
    kontaktModalBeschreibung: (account: string) => `Wird unter „${account}" angelegt`,
    kontaktAngelegt: (name: string) => `${name} wurde in Salesforce angelegt.`,
    kontaktFallbackName: "Kontakt",
    kontaktAccount: (account: string) => `Account: ${account}`,
    kontaktNichtGespiegelt:
      "Der Kontakt ist angelegt, erscheint in Atlas aber erst mit dem nächsten Abgleich.",
    kontaktVorschlagFehler502:
      "Der Vorschlag konnte nicht erzeugt werden. Bitte erneut versuchen.",
    kontaktVorschlagFehler: "Der Vorschlag konnte nicht geladen werden.",
    kontaktAnlegenFehler: "Der Kontakt konnte nicht angelegt werden.",
    kontaktOhneNachname: "Ohne Nachnamen kann Salesforce den Kontakt nicht anlegen.",
    pruefenHinweis:
      "Bitte prüfen und bei Bedarf korrigieren. Leere Felder bleiben leer — geraten wird nichts.",
    // Lead
    leadBekannt: "Diese Adresse ist in Salesforce bereits als Lead erfasst.",
    leadEinstieg: "Zu diesem Absender gibt es keinen Account in Salesforce.",
    leadAnlegen: "Als Lead anlegen",
    leadModalTitel: "Lead in Salesforce anlegen",
    leadModalBeschreibung: (email: string) => `Für ${email}`,
    leadKnopf: "Lead anlegen",
    leadAngelegt: (name: string) => `${name} wurde in Salesforce angelegt.`,
    leadFallbackName: "Der Lead",
    leadVorschlagFehler: "Der Vorschlag konnte nicht geladen werden.",
    leadAnlegenFehler: "Der Lead konnte nicht angelegt werden.",
    leadOhneNachname: "Ohne Nachnamen kann Salesforce den Lead nicht anlegen.",
    leadPruefenHinweis:
      "Bitte prüfen und bei Bedarf korrigieren. Leere Felder bleiben leer — geraten wird nichts. Die E-Mail-Adresse setzt Salesforce selbst aus dem Mail-Kopf.",
    rechercheLaeuft: "Recherche läuft … (dauert etwa 25 Sekunden)",
    rechercheFertig: (n: number) =>
      `Recherche fertig, ${n} ${n === 1 ? "Feld" : "Felder"} gefüllt.`,
    rechercheFehler:
      "Die Recherche ist fehlgeschlagen. Der Lead ist angelegt und kann in Salesforce von Hand ergänzt werden.",
    rechercheOffen:
      "Der Lead ist angelegt. Die Recherche läuft noch — ihr Ergebnis steht später in Salesforce.",
  },
};

export type MailKatalog = typeof mail;
